/**
 * DataHonestyBadges — compact 6-badge cluster showing which page
 * sections are backed by a primary source vs left honestly blank.
 * Rendered near the top of every degree page, right below the
 * Professor's summary tile.
 *
 * Trust levels per section:
 *   verified  (emerald) — evidence.source cites a real URL + not editorial
 *   aggregate (indigo)  — cited but at national/university-wide scope
 *   editorial (amber)   — editorial estimate (labelled, no fabricated data)
 *   unverified (stone)  — honestly blank, no data claimed
 */
import { CheckCircle2, Globe2, Info, MinusCircle } from "lucide-react";

const SECTIONS = [
  { key: "admissions",  label: "Admissions" },
  { key: "cost",        label: "Cost" },
  { key: "curriculum",  label: "Curriculum" },
  { key: "teaching",    label: "Teaching" },
  { key: "outcomes",    label: "Outcomes" },
  { key: "international", label: "International" },
];

function classifyEvidence(ev) {
  if (!ev || typeof ev !== "object") return "unverified";
  const source = (ev.source || "").trim();
  const url = (ev.url || "").trim();
  const scope = (ev.scope || "").trim();
  if (!source) return "unverified";
  if (/editorial estimate/i.test(source) && !url) return "editorial";
  if (scope === "national_field_aggregate" || scope === "university_wide_aggregate") return "aggregate";
  if (url && !/editorial estimate/i.test(source)) return "verified";
  if (source && !url) return "editorial";
  return "verified";
}

function classifyAdmissions(d) {
  const a = d.admissions || {};
  if (a.evidence) return classifyEvidence(a.evidence);
  const er = d.entry_requirements || {};
  const anyEntryEvidence = Object.values(er).find(
    (v) => v && typeof v === "object" && v.evidence
  );
  if (anyEntryEvidence) return classifyEvidence(anyEntryEvidence.evidence);
  if (a.offer_rate != null) return "verified";
  return "unverified";
}

function classifyCurriculum(d) {
  const c = d.curriculum || {};
  if (c.evidence) return classifyEvidence(c.evidence);
  const meaningfulKeys = Object.keys(c).filter((k) => k !== "evidence" && c[k] != null);
  return meaningfulKeys.length ? "verified" : "unverified";
}

function classifyTeaching(d) {
  const t = d.teaching || {};
  const meaningfulKeys = Object.keys(t).filter((k) => k !== "evidence" && t[k] != null);
  if (!meaningfulKeys.length) return "unverified";
  if (t.evidence) return classifyEvidence(t.evidence);
  return "verified";
}

function classifyOutcomes(d) {
  const o = d.outcomes || {};
  // UK records use `outcomes.source` + `outcomes.source_url` (LEO schema);
  // IE/SG/UAE/etc use `outcomes.evidence.{source,url}`. Check both.
  if (o.source && o.source_url) {
    if (/national.*field|university.*wide/i.test(o.source)) return "aggregate";
    return "verified";
  }
  if (!o.evidence && !o.employment_rate && !o.median_starting_salary_usd
      && !o.median_starting_salary_gbp && !o.median_earnings_yr3_gbp
      && !o.sustained_employment_pct) {
    return "unverified";
  }
  return classifyEvidence(o.evidence);
}

function classifyCost(d) {
  const c = d.cost || {};
  if (c.tuition_intl_annual_usd || c.tuition_domestic_annual_usd) {
    return classifyEvidence(c.evidence) === "unverified" ? "verified" : classifyEvidence(c.evidence);
  }
  return "unverified";
}

function classifyInternational(d) {
  const i = d.international || {};
  if (i.evidence) return classifyEvidence(i.evidence);
  if (i.visa_eligible != null || i.psw_eligible_years != null) return "verified";
  return "unverified";
}

const CLASSIFIERS = {
  admissions: classifyAdmissions,
  cost: classifyCost,
  curriculum: classifyCurriculum,
  teaching: classifyTeaching,
  outcomes: classifyOutcomes,
  international: classifyInternational,
};

const TONE = {
  verified:   { bg: "bg-emerald-50", ring: "ring-emerald-200",  text: "text-emerald-900", dot: "bg-emerald-500", label: "Primary source" },
  aggregate:  { bg: "bg-indigo-50",  ring: "ring-indigo-200",   text: "text-indigo-900",  dot: "bg-indigo-500",  label: "Aggregate source" },
  editorial:  { bg: "bg-amber-50",   ring: "ring-amber-200",    text: "text-amber-900",   dot: "bg-amber-500",   label: "Editorial estimate" },
  unverified: { bg: "bg-stone-100",  ring: "ring-stone-200",    text: "text-stone-500",   dot: "bg-stone-400",   label: "Not yet verified" },
};

export default function DataHonestyBadges({ degree }) {
  if (!degree) return null;
  const classified = SECTIONS.map((s) => ({
    ...s,
    tier: CLASSIFIERS[s.key](degree),
  }));

  const verified = classified.filter((c) => c.tier === "verified").length;
  const aggregate = classified.filter((c) => c.tier === "aggregate").length;
  const editorial = classified.filter((c) => c.tier === "editorial").length;

  return (
    <div
      className="mt-4 mb-2 border border-stone-200 rounded-lg p-4 bg-white"
      data-testid="data-honesty-badges"
    >
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <div className="label-uppercase text-[10px] text-stone-700">
            Data honesty report card
          </div>
        </div>
        <div className="text-xs text-stone-500">
          <span className="text-emerald-700 font-medium">{verified}</span> primary
          {" · "}
          <span className="text-indigo-700 font-medium">{aggregate}</span> aggregate
          {" · "}
          <span className="text-amber-700 font-medium">{editorial}</span> editorial
          {" · "}
          <span className="text-stone-500 font-medium">
            {6 - verified - aggregate - editorial}
          </span>{" "}
          blank
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        {classified.map(({ key, label, tier }) => {
          const t = TONE[tier];
          return (
            <a
              key={key}
              href={`#${key}`}
              className={`${t.bg} ${t.text} rounded px-2 py-1.5 ring-1 ${t.ring} hover:ring-2 transition-all block`}
              data-testid={`data-honesty-${key}`}
              title={t.label}
            >
              <div className="flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${t.dot} shrink-0`} />
                <span className="text-[11px] font-medium truncate">{label}</span>
              </div>
              <div className="text-[9px] uppercase tracking-wider mt-0.5 opacity-75">
                {t.label}
              </div>
            </a>
          );
        })}
      </div>
      <div className="text-[10px] text-stone-400 mt-2 flex items-center gap-1">
        <Info className="w-3 h-3" />
        <a href="/methodology/data-sources" className="underline hover:text-stone-600">
          See every primary source powering WUA
        </a>
      </div>
    </div>
  );
}
